/**
 * Shared list plumbing for the full-result screens (Today, Send History, Documents):
 * server-filtered, server-sorted keyset paging (PagedQuery), exact capped counts,
 * debounced search, and a virtualized row container so only visible rows render.
 */
import { retryRead } from '@miraops/contracts';
import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore, type ReactNode } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { PagedQuery, countQuery, type CountResult, type Filter, type Sort, type Transport } from '@miraops/contracts';
import { transport } from './session.ts';
import { quietly } from './quietly.ts';

export function useDebounced<T>(value: T, ms = 250): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => { const id = setTimeout(() => setDebounced(value), ms); return () => clearTimeout(id); }, [value, ms]);
  return debounced;
}

/** Page every matching row using the owning transport. Refresh retains listeners;
 * only replacement or unmount disposes the controller and its subscription set. */
export function usePaged<Raw extends { id: string }>(workspaceId: string, dataset: string, filters: Filter[], sorts: Sort[], scope: string | null = null, refreshKey = 0, api:Transport = transport) {
  const specKey = JSON.stringify([dataset, filters, sorts, scope]);
  const query = useMemo(() => new PagedQuery<Raw, Raw>({ transport:api, workspaceId, spec: { dataset, scope, filters, sorts, pageSize: 100 }, key: r => r.id, map: r => r }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [workspaceId, specKey, api]);
  useEffect(() => { void query.refresh(); }, [query, refreshKey]);
  useEffect(() => () => query.dispose(), [query]);
  const state = useSyncExternalStore(useCallback(cb => query.subscribe(cb), [query]), () => query.state);
  return { query, state };
}

/** Exact count for a filter set (capped server-side); null while counting or after a failure. */
export function useCount(workspaceId: string, dataset: string, filters: Filter[], refreshKey: unknown = 0, api:Transport = transport): CountResult | null {
  const [result, setResult] = useState<CountResult | null>(null);
  const key = JSON.stringify([dataset, filters]);
  useEffect(() => {
    const abort = new AbortController();
    setResult(null);
    retryRead(() => countQuery(api, workspaceId, { dataset, filters: JSON.parse(key)[1] }, abort.signal)).then(setResult).catch(quietly);
    return () => abort.abort();
  }, [workspaceId, key, refreshKey, api]);
  return result;
}

/** Virtualized rows: renders only what is on screen and asks for more near the end. */
export function VirtualRows<T>({ rows, height, render, getKey, onNearEnd, className = '' }: {
  rows: readonly T[]; height: number; render: (row: T, index: number) => ReactNode; getKey: (row: T) => string; onNearEnd?: () => void; className?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const v = useVirtualizer({ count: rows.length, getScrollElement: () => ref.current, estimateSize: () => height, overscan: 10, getItemKey: i => getKey(rows[i]!) });
  const items = v.getVirtualItems();
  const last = items.at(-1)?.index ?? 0;
  useEffect(() => { if (onNearEnd && rows.length && last >= rows.length - 15) onNearEnd(); }, [last, rows.length, onNearEnd]);
  return (
    <div className={`vrows ${className}`} ref={ref}>
      <div style={{ height: v.getTotalSize(), position: 'relative' }}>
        {items.map(item => (
          <div key={item.key} data-index={item.index} style={{ position: 'absolute', top: 0, left: 0, right: 0, transform: `translateY(${item.start}px)` }}>
            {render(rows[item.index]!, item.index)}
          </div>
        ))}
      </div>
    </div>
  );
}
